import Logo from "@/components/Logo";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";

// NOTE: These are structured templates for an Australian fintech education platform.
// They are NOT legal advice and must be reviewed by a qualified Australian lawyer before launch.
const UPDATED = "Last updated: July 2026";

const SECTIONS = [
  {
    id: "company", title: "Company & Contact Details",
    body: [
      "This platform is operated by <strong>Mind Before Markets</strong> (\"MBM\", \"we\", \"us\"), based in Melbourne, Victoria, Australia.",
      "Australian Business Number (ABN): <strong>65 440 168 907</strong>.",
      "Contact: <strong>support@mindbeforemarkets.com</strong>. For privacy matters, mark your message for the attention of the Privacy Officer; for complaints, mark it for the Complaints team.",
    ],
  },
  {
    id: "risk", title: "Risk Warning & Financial Services Disclaimer",
    body: [
      "Mind Before Markets (\"MBM\", \"we\", \"us\") is an educational and mentorship business. We are NOT a licensed provider of financial services and do NOT hold an Australian Financial Services Licence (AFSL).",
      "Nothing on this platform constitutes financial product advice, personal advice, general financial advice, investment advice, or a recommendation to buy, sell or hold any financial product. We do not consider your objectives, financial situation or needs.",
      "We do not provide broker services, trade execution, portfolio management, managed investment schemes, or MBM Trade Setups as recommendations. All lessons, briefings, tools, indicators, setups, AI outputs and community discussion are provided for education, information and decision-support only.",
      "Trading and investing in futures, foreign exchange, shares, derivatives and cryptocurrencies involves a high level of risk and can result in the loss of some or all of your capital. It is not suitable for everyone. Past performance is not a reliable indicator of future performance. We do not guarantee any profit, income, return or funded-account outcome.",
      "You should obtain independent, licensed financial, legal and tax advice before making any financial decision. You act on any information from MBM entirely at your own risk.",
    ],
  },
  {
    id: "educational", title: "Educational Disclaimer",
    body: [
      "All MBM content — including the Trading Trainer curriculum, quizzes, MBM Intelligence briefings, MBM Indicators, MBM Trade Setups, analytics and coaching — is designed to teach trading concepts, psychology, risk management and discipline.",
      "Examples, chart illustrations and hypothetical scenarios are for teaching purposes only and do not represent actual trades or advice. Your results depend entirely on your own decisions, skill, discipline and market conditions.",
    ],
  },
  {
    id: "ai", title: "AI Disclaimer",
    body: [
      "Some MBM features use artificial intelligence (including large language models) to generate educational content, summaries, coaching responses and lesson drafts.",
      "AI outputs are generated automatically, may be inaccurate, incomplete or out of date, and must not be relied upon as fact or as financial, legal or professional advice. AI responses are educational information only and are not personal advice.",
      "Do not share confidential, sensitive or personal information you would not want processed by a third-party AI provider. Always verify AI-generated information independently before acting on it.",
    ],
  },
  {
    id: "terms", title: "Terms & Conditions",
    body: [
      "By accessing or using the MBM platform, website or app, you agree to these Terms. If you do not agree, you must not use the platform.",
      "Eligibility: You must be at least 18 years old and legally able to form a binding contract to purchase any membership or subscription.",
      "Accounts: You are responsible for keeping your login credentials secure and for all activity under your account. Notify us immediately of any unauthorised use.",
      "Acceptable use: You agree not to misuse the platform, share paid content, attempt to bypass access controls or entitlements, scrape or reverse-engineer the platform, or use it for unlawful purposes.",
      "Availability: We aim for high availability but do not guarantee the platform will be uninterrupted or error-free. Features may change, be added or be removed.",
      "Limitation of liability: To the maximum extent permitted by law, MBM is not liable for any trading losses, indirect or consequential loss arising from your use of the platform. Nothing in these Terms excludes rights you have under the Australian Consumer Law that cannot lawfully be excluded.",
      "Governing law: These Terms are governed by the laws of Victoria, Australia, and you submit to the non-exclusive jurisdiction of its courts.",
    ],
  },
  {
    id: "subscriptions", title: "Subscription & Membership Terms",
    body: [
      "Trading Trainer and optional add-ons (MBM Intelligence, MBM Indicators, MBM Trade Setups) are recurring subscriptions billed in advance on a weekly, monthly or yearly cycle until cancelled.",
      "MBM Beginner, Advanced and Professional are fixed-duration coaching programs (not ongoing subscriptions) billed monthly for the stated program length. MBM Elite is application-only.",
      "Prices are in Australian Dollars (AUD) and include GST where applicable. We may change pricing on a prospective basis; existing paid periods are not affected.",
      "Cancellation: You may cancel a recurring subscription at any time; access continues until the end of the current paid period and is not pro-rated. Coaching programs run for their committed duration.",
      "Payments are processed by our third-party payment provider (Stripe). We do not store your full card details.",
    ],
  },
  {
    id: "refunds", title: "Refund & Cancellation Policy (Australian Consumer Law)",
    body: [
      "Our services come with guarantees that cannot be excluded under the Australian Consumer Law (ACL). If a service is not provided with due care and skill, or is not fit for a purpose we agreed, you may be entitled to a remedy.",
      "Because digital educational content is made available immediately, we do not offer refunds simply for change of mind once content has been accessed, except where required by law.",
      "If you believe a service was faulty, materially not as described, or not delivered, contact us within a reasonable time and we will assess your claim and provide a remedy where required by the ACL.",
      "Coaching program fees may be non-refundable for sessions already delivered; unused, undelivered portions will be handled in line with your ACL rights.",
      "To request a refund or raise a consumer-law concern, use the Complaints process below.",
    ],
  },
  {
    id: "privacy", title: "Privacy Policy (Australian Privacy Act & APPs / GDPR-ready)",
    body: [
      "This policy explains how MBM collects, uses, stores and discloses personal information, consistent with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs).",
      "What we collect: your name, email, phone (if provided), account credentials (stored only as a secure hash), membership and payment status, lesson progress, journal entries, uploaded images, AI conversations, community posts, and technical data such as IP address and device/browser information.",
      "Why we collect it: to provide and personalise the platform, process payments, manage memberships and entitlements, provide coaching and support, improve our services, and meet legal obligations.",
      "Disclosure: we share data with trusted processors only as needed — our payment provider (Stripe), hosting/infrastructure, and AI providers (for features you use). We do not sell your personal information.",
      "Storage & security: data is stored on managed infrastructure with access controls. Passwords are hashed; we use encryption in transit. No system is perfectly secure.",
      "Your rights: you may request access to, or correction of, your personal information, and may complain if you believe we have breached the APPs. Where the GDPR applies, you also have rights to erasure, portability and objection; our lawful bases are contract performance, consent and legitimate interests.",
      "International users: by using the platform you consent to your data being processed in Australia and other jurisdictions where our processors operate.",
      "Contact our Privacy Officer using the details in the Contact section to exercise any right.",
    ],
  },
  {
    id: "cookies", title: "Cookie Policy",
    body: [
      "We use strictly necessary cookies to keep you logged in (secure, HTTP-only session cookies) and to operate the platform. These are essential and cannot be disabled without breaking core functionality.",
      "We may use limited analytics to understand usage and improve the platform. Where required by law we will seek your consent for non-essential cookies.",
      "You can control cookies through your browser settings; blocking essential cookies may prevent you from signing in.",
    ],
  },
  {
    id: "aup", title: "Acceptable Use & Community Guidelines",
    body: [
      "The MBM Community is a respectful, education-focused space. You must not post unlawful, abusive, harassing, hateful, misleading or spam content, or share others' personal information.",
      "Do not provide financial advice, solicit funds, promote pump-and-dump schemes, sell trade recommendations, or impersonate MBM staff or coaches. Chart discussion is educational only.",
      "You are solely responsible for content you post. You grant MBM a licence to host and display your content on the platform. We may moderate, remove content, and warn, suspend or ban accounts that breach these guidelines.",
      "Report harmful content or users through the platform; we review reports and take proportionate action.",
    ],
  },
  {
    id: "ip", title: "Intellectual Property & Copyright",
    body: [
      "All MBM content — including course material, videos, PDFs, the Trading Trainer curriculum, MBM Indicators, MBM Trade Setups, branding, logos, and AI-assisted material published by MBM — is owned by or licensed to Mind Before Markets and protected by copyright and other laws.",
      "Your membership grants a personal, non-transferable, revocable licence to access content for your own learning. You may not copy, redistribute, resell, publicly share or create derivative works without our written permission.",
      "\"Mind Before Markets\", \"MBM\" and associated logos are our trade marks. Unauthorised use is prohibited.",
    ],
  },
  {
    id: "complaints", title: "Complaint Handling Policy",
    body: [
      "We take complaints seriously. To raise a complaint (including consumer-law or privacy concerns), contact us using the details in the Contact section with a description of the issue.",
      "We will acknowledge your complaint promptly and aim to resolve it within a reasonable time. If you are not satisfied, you may escalate to the relevant external body (for privacy, the Office of the Australian Information Commissioner; for consumer matters, your state fair-trading office or the ACCC).",
    ],
  },
  {
    id: "data-retention", title: "Data Retention",
    body: [
      "We keep personal information only as long as necessary for the purposes described in the Privacy Policy, or as required by law (for example, records needed for tax or dispute purposes).",
      "When you close your account, we will delete or de-identify personal information that we no longer need, subject to legal retention obligations.",
    ],
  },
  {
    id: "minors", title: "Children & Minors",
    body: [
      "The platform is intended for adults aged 18 and over. We do not knowingly collect personal information from children. If we learn we have collected information from a minor, we will delete it.",
    ],
  },
  {
    id: "accessibility", title: "Accessibility Statement",
    body: [
      "We are committed to making the platform usable for as many people as possible and aim to align with recognised accessibility guidelines (WCAG). If you experience an accessibility barrier, contact us and we will work to assist you.",
    ],
  },
];

export default function Legal() {
  const [active, setActive] = useState(SECTIONS[0].id);
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
          <span className="text-zinc-700">/</span>
          <span className="section-label text-[10px] text-[#9fd3ff]">Legal & Compliance</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10" data-testid="legal-page">
        <div className="glass-card glass-card-featured p-5 flex items-start gap-3 mb-10" data-testid="legal-risk-banner">
          <ShieldAlert className="h-5 w-5 text-[#e6c67a] shrink-0 mt-0.5" />
          <p className="text-[13px] text-zinc-300 font-light leading-relaxed">
            MBM is an <strong className="text-white">educational and mentorship platform</strong>. We are not licensed
            financial advisers, we do not hold an AFSL, and nothing here is financial, investment or trading advice.
            Trading involves substantial risk of loss and no profit is guaranteed. See the full Risk Warning below.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-1">
              {SECTIONS.map((s) => (
                <a key={s.id} href={`#${s.id}`} onClick={() => setActive(s.id)}
                  className={`block text-[13px] font-light py-1.5 transition-colors ${active === s.id ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                  data-testid={`legal-nav-${s.id}`}>
                  {s.title}
                </a>
              ))}
            </div>
          </aside>

          <div className="space-y-14 min-w-0">
            <p className="section-label text-[9px] text-zinc-600">{UPDATED}</p>
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`legal-section-${s.id}`}>
                <h2 className="font-heading text-2xl sm:text-3xl font-light tracking-tight">{s.title}</h2>
                <div className="mt-5 space-y-4">
                  {s.body.map((p, i) => (
                    <p key={i} className="text-[14px] text-zinc-400 font-light leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                </div>
              </section>
            ))}
            <p className="text-xs text-zinc-600 font-light pt-6 border-t border-white/8">
              These documents are provided as templates and do not constitute legal advice. Mind Before Markets recommends
              obtaining advice from a qualified Australian lawyer before relying on them. © {new Date().getFullYear()} Mind Before Markets, Melbourne, Australia.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
